import { LazyQueryResultTuple, MutationTuple, QueryResult, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useCallback, useContext } from 'react';
import { downloadFile } from 'src/helpers/files';
import { GENERATE_TENJIN_ANSWER, GET_TENJIN_DOWNLOAD_URL, GET_TENJIN_FILES, GET_TENJIN_UPLOAD_URL } from './queries';
import {
  ApiTenjinAnswerRequest,
  ApiTenjinAnswerResponse,
  ApiTenjinDownloadUrlResponse,
  ApiTenjinFileRequest,
  ApiTenjinFilesResponse,
  ApiTenjinUploadUrlResponse
} from './types';
import { GenerateResponseContext, GenerateResponseContextValue } from './context';

export function useTenjinFiles(): QueryResult<ApiTenjinFilesResponse> {
  return useQuery<ApiTenjinFilesResponse>(GET_TENJIN_FILES);
}

export function useLazyTenjinDownloadUrl(): LazyQueryResultTuple<ApiTenjinDownloadUrlResponse, ApiTenjinFileRequest> {
  return useLazyQuery<ApiTenjinDownloadUrlResponse, ApiTenjinFileRequest>(GET_TENJIN_DOWNLOAD_URL);
}

export function useLazyTenjinUploadUrl(): LazyQueryResultTuple<ApiTenjinUploadUrlResponse, ApiTenjinFileRequest> {
  return useLazyQuery<ApiTenjinUploadUrlResponse, ApiTenjinFileRequest>(GET_TENJIN_UPLOAD_URL);
}

export function useDownloadTenjinFile(): { getTenjinFile: (targetPath: string) => void; loading: boolean } {
  const [getFileLazy, { loading }] = useLazyTenjinDownloadUrl();

  const getTenjinFile = useCallback(
    (targetPath: string) => {
      getFileLazy({ variables: { targetPath } }).then(data => {
        if (!data.data?.getSignedUrlDownloadTenjin) return;
        downloadFile(data.data.getSignedUrlDownloadTenjin, targetPath);
      });
    },
    [getFileLazy]
  );

  return {
    getTenjinFile,
    loading
  };
}

export function useGenerateTenjinAnswer(): MutationTuple<ApiTenjinAnswerResponse, ApiTenjinAnswerRequest> {
  return useMutation<ApiTenjinAnswerResponse, ApiTenjinAnswerRequest>(GENERATE_TENJIN_ANSWER);
}

export function useGenerateResponse(): GenerateResponseContextValue {
  const ctx = useContext(GenerateResponseContext);
  if (!ctx) {
    throw new Error('not in GenerateResponseContext provider');
  }

  return ctx;
}
